import { ChangeEvent, useEffect, useState } from "react";
import { Software } from "../models/Softwares/Software";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import agent from "../../api/agent";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SignalCellularOffIcon from "@mui/icons-material/SignalCellularOff";
import GetAppIcon from "@mui/icons-material/GetApp";
import { SoftwareCreate } from "../models/Softwares/SoftwareCreate";
import CreateSoftwareForm from "../forms/Softwares/CreateSoftwareForm";
import EditSoftwareForm from "../forms/Softwares/EditSoftwareForm";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import { jwtDecode } from "jwt-decode";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import { OfflineLicense } from "../models/Licenses/OfflineLicense";
import GenerateOfflineLicForm from "../forms/Licenses/GenerateOfflineLicForm";

export default function SoftwareDashboard() {
  const [softwareList, setSoftwareList] = useState<Software[]>([]);
  const [filteredSoftwareList, setFilteredSoftwareList] = useState<Software[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [createFormOpenState, setCreateFormOpenState] =
    useState<boolean>(false);
  const [editFormOpenState, setEditFormOpenState] = useState<boolean>(false);
  const [deleteDialogOpenState, setDeleteDialogOpenState] =
    useState<boolean>(false);
  const [selectedSoftware, setSelectedSoftware] =
    useState<SoftwareCreate | null>(null);
  const [selectedSoftwareId, setSelectedSoftwareId] = useState<number>(0);

  // For Offline license generate
  const [offlineGenerateFormOpenState, setOfflineGenerateFormOpenState] =
    useState<boolean>(false);
  const [selectedOfflineSoftwareId, setSelectedOfflineSoftwareId] =
    useState<number>(0);
  const [prefixName, setPrefixName] = useState<string>("");

  // For Search function(Store the user input)
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Authentication
  const [token, setToken] = useState<string>("");

  // Authorization
  const [userRole, setUserRole] = useState<string>("");

  // For fileName
  const [username, setUsername] = useState<string>("");

  // For export to CSV
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);
          const decodedToken: any = jwtDecode(tokenFLocal);
          const userId = decodedToken.sub;
          // to get the user role for user logged in
          const userResponse = await agent.User.getUserDetail(
            userId,
            tokenFLocal,
          );
          setUserRole(userResponse.role);

          const username = decodedToken.name;
          const trimmedUsername = username.replace(/\s+/g, "");
          setUsername(trimmedUsername);

          // get the software list details from DB
          const softwareResponse = await agent.Softwares.list(tokenFLocal);
          setSoftwareList(softwareResponse);
          setFilteredSoftwareList(softwareResponse);

          // close the loader after finish fetching data
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // function to open create form
  function handleCreateFormOpen() {
    setCreateFormOpenState(true);
  }

  function handleCreateOrEditFormClose() {
    setCreateFormOpenState(false);
    setEditFormOpenState(false);
    setSelectedSoftware(null);
  }

  function handleDeleteOnClick(softwareId: number) {
    setSelectedSoftwareId(softwareId);
    setDeleteDialogOpenState(true);
  }

  function handleDeleteDialogClose() {
    setSelectedSoftwareId(0);
    setDeleteDialogOpenState(false);
  }

  // function to open edit form(software)
  function handleEditFormOpen(softwareId: number) {
    const getSoftware = softwareList.find((x) => x.softwareId === softwareId);
    if (getSoftware) {
      // Map Software to SoftwareCreate
      const SoftwareCreate: SoftwareCreate = {
        softwareName: getSoftware.softwareName,
        prefixName: getSoftware.prefixName,
        description: getSoftware.description,
        // Add other properties as needed, excluding unitTypeId if not in UnitTypeCreate
      };
      setSelectedSoftware(SoftwareCreate);
      setSelectedSoftwareId(softwareId);
      setEditFormOpenState(true);
    }
  }

  // Function to create new Software record
  async function handleCreateSubmit(formData: SoftwareCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.Softwares.create(formData, token).then(async () => {
        const updatedSoftware = await agent.Softwares.list(token);
        setSoftwareList(updatedSoftware);
        handleCreateOrEditFormClose();
        showToast("Software Created Successfully", "success");
      });
    } catch (error) {
      console.error("Error Create Software Record:", error);
      showToast("Error Create Software Record", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to Update Software record
  async function handleEditSubmit(formData: SoftwareCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.Softwares.update(selectedSoftwareId, formData, token).then(
        async () => {
          const updatedSoftware = await agent.Softwares.list(token);
          setSoftwareList(updatedSoftware);
          handleCreateOrEditFormClose();
          showToast("Software Edit Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error Updating Software Record:", error);
      showToast("Error Updating Software Record", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to Delete Software record
  async function handleConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedSoftwareId === 0) return;
    setLoading(true);
    try {
      await agent.Softwares.delete(selectedSoftwareId, token).then(async () => {
        const updatedSoftware = await agent.Softwares.list(token);
        setSoftwareList(updatedSoftware);
        handleDeleteDialogClose();
        showToast("Software Delete Successfully", "success");
      });
    } catch (error) {
      console.error("Error Deleting Software Record:", error);
      showToast("Error Deleting Software Record", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose(); //Reset all the delete form open state
    }
  }

  // Function to open a form for user input valid date
  function handleOfflineUserInputFormOpen(softwareId: number, prefix: string) {
    setOfflineGenerateFormOpenState(true);
    setSelectedOfflineSoftwareId(softwareId);
    setPrefixName(prefix);
  }

  function handleOfflineUserInputFormClose() {
    setOfflineGenerateFormOpenState(false);
    setSelectedOfflineSoftwareId(0);
    setPrefixName("");
  }

  // Function to download the offline temporary license file
  const handleOfflineLicenseDownloadSubmit = async (
    formData: OfflineLicense,
  ) => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      const response = await agent.License.downloadOfflineLicense(
        formData,
        token,
      );

      // Format the date
      const utcDate = new Date(formData.validUntil); // Parse the UTC ISO string
      const formattedDate = utcDate
        .toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        })
        .replace(/\//g, "");

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${prefixName}_offline_license_${formattedDate}.tmplic`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      alert("License download failed. Please check the License Key.");
    } finally {
      setLoading(false);
      handleOfflineUserInputFormClose();
    }
  };

  // Function to search and filter
  useEffect(() => {
    const filtered = softwareList.filter((software) =>
      software.softwareName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredSoftwareList(filtered);
  }, [searchTerm, softwareList]);

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  // Function for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to export software list to CSV file
  const handleExport = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setExportLoading(true);
    try {
      const response = await agent.Softwares.exportToCSV(token);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      let filename = `software-export-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Exported to CSV successfully", "success");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      showToast("Error exporting to CSV", "error");
    } finally {
      setExportLoading(false);
    }
  };

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Software List</Typography>

        <Stack direction="row" spacing={1}>
          {/* Only super admin can create new software list */}
          {userRole === "Super Admin" && (
            <Tooltip title="Create New" followCursor>
              <Fab
                color="primary"
                onClick={() => handleCreateFormOpen()}
                size="small"
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          )}

          {userRole === "Super Admin" && (
            <Tooltip title="Export to CSV" followCursor>
              <span>
                <Fab
                  color="success"
                  onClick={() => handleExport()}
                  size="small"
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <GetAppIcon />
                  )}
                </Fab>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Box>
        <TextField
          label="Search"
          // margin="normal"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Box>

      {filteredSoftwareList.length === 0 ? (
        <Box sx={{ mt: 5 }}>
          <Divider>
            <Typography variant="button">No Record found</Typography>
          </Divider>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Software Name</TableCell>
                <TableCell>Prefix Name</TableCell>
                <TableCell>Description</TableCell>
                {/* Show action column for Super Admin level */}
                {userRole === "Super Admin" && <TableCell>Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSoftwareList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((software) => (
                  <TableRow key={software.softwareId}>
                    <TableCell>{software.softwareName}</TableCell>
                    <TableCell>{software.prefixName}</TableCell>
                    {software.description !== null || undefined ? (
                      <TableCell>{software.description}</TableCell>
                    ) : (
                      <TableCell></TableCell>
                    )}
                    {/* Show action button for Super Admin level */}
                    {userRole === "Super Admin" && (
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {/* Button to download the offline lic file */}
                          <Tooltip title="Download Temporary License">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOfflineUserInputFormOpen(
                                  software.softwareId,
                                  software.prefixName,
                                )
                              }
                            >
                              <SignalCellularOffIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Software Detail">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() =>
                                handleEditFormOpen(software.softwareId)
                              }
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Software">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() =>
                                handleDeleteOnClick(software.softwareId)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {/* Pagination Component (Company Table) */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
            component="div"
            count={filteredSoftwareList.length} // Total number of rows
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-toolbar": {
                paddingLeft: 2, // Adjust padding as needed
              },
            }}
          />
        </TableContainer>
      )}

      {/* --------------------- Software MODEL------------------------------- */}
      {/* Model for software create form */}
      <Dialog
        open={createFormOpenState}
        onClose={handleCreateOrEditFormClose}
        maxWidth="lg" // Options: 'xs', 'sm', 'md', 'lg', 'xl' (md is larger than original)
        fullWidth // Ensures it takes the full width up to maxWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } }, // Optional: Customize backdrop
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="h2">
            Create New Software
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCreateOrEditFormClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              "&:hover": {
                color: (theme) => theme.palette.grey[700],
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CreateSoftwareForm
            closeForm={handleCreateOrEditFormClose}
            create={handleCreateSubmit}
          />
        </DialogContent>
      </Dialog>

      {/* Model for Maintenance edit form */}
      <Dialog
        open={editFormOpenState}
        onClose={handleCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Edit Software
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedSoftware && (
            <EditSoftwareForm
              selectedSoftware={selectedSoftware}
              closeForm={handleCreateOrEditFormClose}
              edit={handleEditSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Model for Offline License Generate form */}
      <Dialog
        open={offlineGenerateFormOpenState}
        onClose={handleOfflineUserInputFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Enter Valid Until For Temporary License
          </Typography>
        </DialogTitle>
        <DialogContent>
          <GenerateOfflineLicForm
            closeForm={handleOfflineUserInputFormClose}
            generate={handleOfflineLicenseDownloadSubmit}
            selectedSoftwareId={selectedOfflineSoftwareId}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationBox
        open={deleteDialogOpenState}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
